import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Endpoints, Mutations, Actions } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';

@Module({name: 'Localization', namespaced: true})
export default class Localization extends VuexModule {
    public localizationKey: string = '';
    public languageOption: string = 'en';
    public searchOption: any = '';
    public allLanguages: any[] = ['en', 'da', 'es'];
    public languageAllTexts: any[] = [];
    public addLanguageVisible: boolean = false;
    public addLanguageKeyVisible: boolean = false;
    public importLanguageCsvVisible: boolean = false;
    public missingTranslations: { [index: string]: string } = {};

    @Mutation
    public RESET_LOCALIZATION_STORE_STATE() {
        this.localizationKey          = '';
        this.languageOption           = 'en';
        this.searchOption             = '';
        this.allLanguages             = [];
        this.languageAllTexts         = [];
        this.addLanguageVisible       = false;
        this.addLanguageKeyVisible    = false;
        this.importLanguageCsvVisible = false;
        this.missingTranslations      = {};
    }

    get getLocalizationKey() {
        return this.localizationKey;
    }

    get getLanguageOption() {
        return this.languageOption;
    }

    get getAllLanguages() {
        return this.allLanguages;
    }

    get getCurrentLanguageObject() {
        return this.allLanguages.find((lang: any) => lang.key === this.localizationKey);
    }

    get getSearchOption() {
        return this.searchOption;
    }

    get getLanguageAllTexts() {
        return this.languageAllTexts;
    }

    get getAddLanguageVisible() {
        return this.addLanguageVisible;
    }

    get getAddLanguageKeyVisible() {
        return this.addLanguageKeyVisible;
    }

    get getImportLanguageCsvVisible() {
        return this.importLanguageCsvVisible;
    }

    @Action
    public FETCH_ALL_LANGUAGES() {
        return axios.get(Endpoints.Admin.Localization.ALL_LANGUAGES)
            .then((response: any) => {
                store.commit(Mutations.SET_ALL_LANGUAGES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_LANGUAGE_ALL_TEXTS(data: any) {
        axios.get(Endpoints.Admin.Localization.LANGUAGE_TEXTS, {params: {key: data.key, search: data.search}})
            .then((response: any) => {
                store.commit(Mutations.SET_LANGUAGE_ALL_TEXTS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SAVE_LANGUAGE_TEXT(data: any) {
        const requestPayload = new FormData();

        requestPayload.append('id', data.id);
        requestPayload.append('textKey', data.textKey);
        requestPayload.append('content', data.content);
        requestPayload.append('languageKey', data.languageKey);

        axios.post(Endpoints.Admin.Localization.LANGUAGE_TEXT, requestPayload)
            .then((response) => {
                store.commit(Mutations.UPDATE_TEXT_CONTENT, {body: response.data, id: data.id});
                NotifyHelpers.notify('text_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_LANGUAGE(data: any) {
        const requestPayload = new FormData();

        requestPayload.append('key', data.languageKey);
        requestPayload.append('local', data.localName);
        requestPayload.append('english', data.englishName);

        axios.post(Endpoints.Admin.Localization.ADD_LANGUAGE, requestPayload)
            .then(() => {
                store.commit(Mutations.SET_ADD_LANGUAGE_VISIBILITY, false);
                store.dispatch(Actions.FETCH_ALL_LANGUAGES);
                NotifyHelpers.notify('language_has_been_added', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_LANGUAGE_NEW_KEY(data: any) {
        const requestPayload = new FormData();

        requestPayload.append('key', data.key);
        requestPayload.append('prefix', data.prefix);

        axios.post(Endpoints.Admin.Localization.LANGUAGE_KEY, requestPayload)
            .then((response) => {
                store.commit(Mutations.SET_ADD_LANGUAGE_KEY_VISIBILITY, false);
                store.commit(Mutations.ADD_LANGUAGE_KEY, response.data);
                NotifyHelpers.notify('language_key_has_been_added', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public IMPORT_LANGUAGE_CSV(data: any) {
        const requestPayload = new FormData();

        requestPayload.append('attachedFile', data.attchedFile);
        requestPayload.append('lang', data.lang);

        axios.post(Endpoints.Admin.Localization.IMPORT_LANGUAGE_CSV, requestPayload)
            .then(() => {
                store.commit(Mutations.SET_IMPORT_LANGUAGE_CSV_VISIBILITY, false);
                store.dispatch(Actions.FETCH_LANGUAGE_ALL_TEXTS, { key: store.getters['Localization/getLanguageOption'], search: store.getters['Localization/getSearchOption'] });
                NotifyHelpers.notify('csv_has_been_imported', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public GLOBAL_CHANGE_LANGUAGE(data: any) {
        axios.post(Endpoints.Admin.Localization.GLOBAL_CHANGE_LANGUAGE(data.lang))
            .then(() => {
                store.dispatch(Actions.UPDATE_USER_LANGUAGE, data.lang);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FLAG_MISSING_TRANSLATION(localizationQuery: any) {
        if (!store.getters['User/getUser'] || !store.getters['User/getUser'].isHococoStaff) {
            return;
        }

        store.state.Localization.missingTranslations[localizationQuery.key] = localizationQuery.defaultValue;
    }

    @Action
    public SEND_MISSING_TRANSLATIONS() {
        if (!store.getters['User/getUser'] || !store.getters['User/getUser'].isHococoStaff) {
            return;
        }
        axios.post(Endpoints.Neckbeard.MISSING_TRANSLATIONS, {
            missingTranslations: store.state.Localization.missingTranslations,
        })
        .then(() => {})
        .catch((error) => {
            if (error.response) {
                const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
            } else {
                console.log(error);
            }
        });
    }

    @Mutation
    public SET_ALL_LANGUAGES(data: any) {
        this.allLanguages = data;
    }

    @Mutation
    public SET_LANGUAGE_ALL_TEXTS(data: any) {
        this.languageAllTexts = data;
    }

    @Mutation
    public SET_LOCALIZATION_KEY(value: string) {
        this.localizationKey = value;
    }

    @Mutation
    public UPDATE_TEXT_CONTENT(data: any) {
        const currentText = this.languageAllTexts.find((text: any) => text.id === data.id);
        currentText.body = data.body;
    }

    @Mutation
    public ADD_LANGUAGE_KEY(data: any) {
        this.languageAllTexts.unshift(data);
    }

    @Mutation
    public SET_LANGUAGE_OPTION(value: any) {
        this.languageOption = value;
    }

    @Mutation
    public SET_LOCALIZATION_SEARCH_OPTION(value: any) {
        this.searchOption = value;
    }

    @Mutation
    public SET_ADD_LANGUAGE_VISIBILITY(visible: boolean = true) {
        this.addLanguageVisible = visible;
    }

    @Mutation
    public SET_ADD_LANGUAGE_KEY_VISIBILITY(visible: boolean = true) {
        this.addLanguageKeyVisible = visible;
    }

    @Mutation
    public SET_IMPORT_LANGUAGE_CSV_VISIBILITY(visible: boolean = true) {
        this.importLanguageCsvVisible = visible;
    }
}

